/**
 * Los links de una nota interna: se pintan clickeables, y el de un presupuesto se puede mandar al
 * cliente con un botón desde la misma nota, sin copiarlo al compose.
 */
import { LINK_MARKETPLACE_HASH } from "@/services/XCONS_ENV";
import type { TimelineRow } from "@/services/conversations.api";

const URL_RE = /https?:\/\/[^\s<>()]+/g;

/** Un link sin la puntuación con la que termina la oración («…/uid/abc.»). */
function limpiar(url: string): string {
  return url.replace(/[.,;:!?»"')\]]+$/, "");
}

export type TrozoDeNota = { tipo: "texto"; texto: string } | { tipo: "link"; url: string };

/** El texto partido en texto y links, en orden; lo que queda después de un link sigue como texto. */
export function partirLinks(texto: string): TrozoDeNota[] {
  const trozos: TrozoDeNota[] = [];
  let ultimo = 0;
  for (const m of texto.matchAll(URL_RE)) {
    const url = limpiar(m[0]);
    if (m.index! > ultimo) trozos.push({ tipo: "texto", texto: texto.slice(ultimo, m.index) });
    trozos.push({ tipo: "link", url });
    ultimo = m.index! + url.length;
  }
  if (ultimo < texto.length) trozos.push({ tipo: "texto", texto: texto.slice(ultimo) });
  return trozos;
}

export type TrozoConPresupuesto = TrozoDeNota | { tipo: "presupuesto"; quoteId: number; texto: string };

/** «presupuesto #1839379»: el número lleva al presupuesto en el admin (D59). */
const PRESUPUESTO_RE = /(presupuesto\s+)#(\d+)/gi;

/**
 * Lo que se pinta de una nota: los links, y el número de un «presupuesto #N» como link al
 * presupuesto en el panel. Sólo en las notas: al cliente le llega el texto tal cual.
 */
export function partirNota(texto: string): TrozoConPresupuesto[] {
  const trozos: TrozoConPresupuesto[] = [];
  for (const t of partirLinks(texto)) {
    if (t.tipo !== "texto") {
      trozos.push(t);
      continue;
    }
    let ultimo = 0;
    for (const m of t.texto.matchAll(PRESUPUESTO_RE)) {
      const numero = m.index! + m[1].length;
      if (numero > ultimo) trozos.push({ tipo: "texto", texto: t.texto.slice(ultimo, numero) });
      trozos.push({ tipo: "presupuesto", quoteId: Number(m[2]), texto: `#${m[2]}` });
      ultimo = m.index! + m[0].length;
    }
    if (ultimo < t.texto.length) trozos.push({ tipo: "texto", texto: t.texto.slice(ultimo) });
  }
  return trozos;
}

/** El primer link de presupuesto de la nota, o `null`. */
export function linkDePresupuesto(texto: string): string | null {
  for (const t of partirLinks(texto)) if (t.tipo === "link" && t.url.includes(LINK_MARKETPLACE_HASH)) return t.url;
  return null;
}

/** ¿Ya salió un mensaje al cliente con ese link? Entonces el botón muestra que se mandó. */
export function linkYaEnviado(rows: TimelineRow[], link: string): boolean {
  return rows.some((r) => r.kind === "message" && r.payload.senderType === "operator" && !!r.payload.content?.includes(link));
}

/**
 * Los presupuestos que nombran las notas del hilo —por su link público o por «presupuesto #N»—, en
 * una firma estable. Cuando cambia, el asistente (u otro) dejó uno nuevo en una nota, y el contador
 * de presupuestos del encabezado y el panel lateral se vuelven a cargar (#639).
 */
export function presupuestosEnNotas(rows: TimelineRow[]): string {
  const vistos = new Set<string>();
  const leer = (texto?: string | null) => {
    if (!texto) return;
    for (const t of partirNota(texto)) {
      if (t.tipo === "link" && t.url.includes(LINK_MARKETPLACE_HASH)) vistos.add(t.url);
      if (t.tipo === "presupuesto") vistos.add(`#${t.quoteId}`);
    }
  };
  for (const r of rows) {
    if (r.kind === "note") leer(r.payload.content);
    if (r.kind === "message") for (const n of r.payload.notes ?? []) leer(n.content);
  }
  return [...vistos].sort().join(" ");
}
